import type { Carrier } from './types/common';
import type { OrderShipmentFilters } from './types/order-shipments';
import type { CourierPickupFilters } from './types/pickups';
import type { ShipmentFilters } from './types/shipments';

export type ProductFilters = {
  search?: string | null;
  per_page?: number;
  cursor?: string | null;
  /** Collection GID or numeric id - the backend normalizes GIDs. */
  collection_id?: string | null;
  tag?: string | null;
};
export type ExcludedProductFilters = {
  product_ids?: string[];
  method_key?: string | null;
  country?: string | null;
  status?: 'active' | 'disabled' | null;
  search?: string | null;
  page?: number;
  per_page?: number;
};

export const queryKeys = {
  session: ['session'] as const,
  accounts: {
    all: ['accounts'] as const,
    list: () => ['accounts', 'list'] as const,
  },
  me: ['me'] as const,
  packageTemplates: ['package-templates'] as const,
  customPackages: {
    all: ['custom-packages'] as const,
    list: () => ['custom-packages', 'list'] as const,
  },
  carriers: {
    all: ['carriers'] as const,
    status: (carrier: Carrier) => ['carriers', carrier, 'status'] as const,
    organizations: (carrier: Carrier) => ['carriers', carrier, 'organizations'] as const,
  },
  carrierServices: {
    all: ['carrier-services'] as const,
    list: () => ['carrier-services', 'list'] as const,
  },
  deliveryCatalog: ['delivery-catalog'] as const,
  markets: {
    all: ['markets'] as const,
    list: () => ['markets', 'list'] as const,
    availableMethods: (marketId: number) =>
      ['markets', 'detail', marketId, 'available-methods'] as const,
  },
  excludedProducts: {
    all: ['excluded-products'] as const,
    list: (filters: ExcludedProductFilters) => ['excluded-products', 'list', filters] as const,
  },
  products: {
    all: ['products'] as const,
    list: (filters: ProductFilters) => ['products', 'list', filters] as const,
    facets: ['products', 'facets'] as const,
  },
  orders: {
    all: ['orders'] as const,
    detail: (id: string | number) => ['orders', 'detail', id] as const,
  },
  orderShipments: {
    all: ['order-shipments'] as const,
    list: (filters: OrderShipmentFilters) => ['order-shipments', 'list', filters] as const,
    detail: (id: string | number) => ['order-shipments', 'detail', id] as const,
  },
  shipments: {
    all: ['shipments'] as const,
    list: (filters: ShipmentFilters) => ['shipments', 'list', filters] as const,
  },
  shipmentBatches: {
    all: ['shipment-batches'] as const,
    detail: (id: number | null) => ['shipment-batches', 'detail', id] as const,
  },
  courierPickups: {
    all: ['courier-pickups'] as const,
    list: (filters: CourierPickupFilters) => ['courier-pickups', 'list', filters] as const,
    detail: (id: number | null) => ['courier-pickups', 'detail', id] as const,
    cutoff: (locationId: number | null) => ['courier-pickups', 'cutoff', locationId] as const,
  },
  pickupLocations: {
    all: ['pickup-locations'] as const,
    list: () => ['pickup-locations', 'list'] as const,
  },
  contactPersons: {
    all: ['contact-persons'] as const,
    list: () => ['contact-persons', 'list'] as const,
  },
} as const;
